import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { apiFetch } from "@/lib/api";
import { formatPrice } from "@/lib/formatPrice";
import { cn } from "@/lib/utils";
import PackingList from "@/components/orders/PackingList";
import OrderStatusCard from "@/components/orders/OrderStatusCard";
import CustomerInfoCard from "@/components/orders/CustomerInfoCard";
import ShipmentsHistory from "@/components/orders/ShipmentsHistory";

interface OrderItem {
  id: number;
  product_id: number;
  product_name: string;
  product_sku: string;
  product_bin_location: string | null;
  product_image: string | null;
  price: number;
}

interface Order {
  id: number;
  status: "new" | "processing" | "fulfilled" | "cancelled";
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  zip: string;
  shipping_method_name: string;
  shipping_price: number;
  total: number;
  created_at: string;
  tracking_number: string | null;
  shipping_company: string | null;
  items: OrderItem[];
}

interface Shipment {
  id: number;
  tracking_number: string | null;
  status: string;
  error_message: string | null;
  credential_name: string;
  created_at: string;
}

export default function OrderDetail() {
  const { id } = useParams({ from: "/admin/orders/$id" });
  const queryClient = useQueryClient();
  const [packedItems, setPackedItems] = useState<Set<number>>(new Set());
  const [fulfilledMessage, setFulfilledMessage] = useState(false);

  const { data: order, isLoading } = useQuery<Order>({
    queryKey: ["admin-order", id],
    queryFn: () => apiFetch<Order>(`/api/admin/orders/${id}`),
  });

  const { data: shipments = [] } = useQuery<Shipment[]>({
    queryKey: ["admin-order-shipments", id],
    queryFn: () => apiFetch<Shipment[]>(`/api/admin/orders/${id}/shipments`),
  });

  const statusMutation = useMutation({
    mutationFn: (status: string) =>
      apiFetch(`/api/admin/orders/${id}/status`, {
        method: "PUT",
        body: JSON.stringify({ status }),
      }),
    onSuccess: (_data, status) => {
      queryClient.invalidateQueries({ queryKey: ["admin-order", id] });
      queryClient.invalidateQueries({ queryKey: ["admin-orders"] });
      if (status === "fulfilled") {
        setFulfilledMessage(true);
      }
    },
  });

  const shippingMutation = useMutation({
    mutationFn: (data: Record<string, string | null>) =>
      apiFetch(`/api/admin/orders/${id}/shipping`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-order", id] });
      queryClient.invalidateQueries({ queryKey: ["admin-orders"] });
    },
  });

  function togglePacked(itemId: number) {
    setPackedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  }

  function markAllPacked() {
    if (order) {
      setPackedItems(new Set(order.items.map((i) => i.id)));
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <span className="text-sm text-muted-foreground">Učitavanje...</span>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground">Narudžba nije pronađena.</p>
        <Link to="/admin/orders" className="text-sm text-foreground underline mt-2 inline-block">
          Nazad na narudžbe
        </Link>
      </div>
    );
  }

  const allPacked = order.items.length > 0 && packedItems.size === order.items.length;
  const itemsSubtotal = order.total - order.shipping_price;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <Link
          to="/admin/orders"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="size-4" />
          Sve narudžbe
        </Link>
        <div className="flex items-center gap-3 sm:ml-auto">
          <h1 className="text-xl font-semibold">Narudžba #{order.id}</h1>
          <span className={cn("text-xs font-medium px-2.5 py-1 rounded-full", "")}>
            {order.status}
          </span>
        </div>
      </div>

      {fulfilledMessage && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-4 flex items-start gap-3">
          <div className="size-8 rounded-full bg-emerald-100 flex items-center justify-center shrink-0 mt-0.5">
            <Mail className="size-4 text-emerald-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-emerald-800">Narudžba isporučena</p>
            <p className="text-sm text-emerald-700 mt-0.5">
              Kupac <span className="font-medium">{order.email}</span> je obaviješten putem emaila.
            </p>
          </div>
          <button
            onClick={() => setFulfilledMessage(false)}
            className="ml-auto text-emerald-400 hover:text-emerald-600 transition-colors text-lg leading-none"
          >
            &times;
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <PackingList
            items={order.items}
            packedItems={packedItems}
            onTogglePacked={togglePacked}
            onMarkAllPacked={markAllPacked}
            allPacked={allPacked}
            orderStatus={order.status}
            onFulfill={async () => {
              if (order.status === "new") {
                await apiFetch(`/api/admin/orders/${id}/status`, {
                  method: "PUT",
                  body: JSON.stringify({ status: "processing" }),
                });
              }
              statusMutation.mutate("fulfilled");
            }}
            isFulfilling={statusMutation.isPending}
          />

          <div className="bg-white rounded-xl border border-border/60 overflow-hidden">
            <div className="px-5 py-3 border-b border-border/40">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Pregled</h3>
            </div>
            <div className="p-5 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Artikli ({order.items.length})</span>
                <span>{formatPrice(itemsSubtotal)} KM</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Dostava ({order.shipping_method_name})</span>
                <span>{order.shipping_price === 0 ? <span className="text-emerald-600">Besplatno</span> : `${formatPrice(order.shipping_price)} KM`}</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-border/40 font-semibold text-base">
                <span>Ukupno</span>
                <span>{formatPrice(order.total)} KM</span>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <OrderStatusCard
            status={order.status}
            createdAt={order.created_at}
            orderId={order.id}
            onChangeStatus={(status) => statusMutation.mutate(status)}
            isPending={statusMutation.isPending}
          />

          <CustomerInfoCard
            firstName={order.first_name}
            lastName={order.last_name}
            email={order.email}
            phone={order.phone}
            address={order.address}
            city={order.city}
            zip={order.zip}
            shippingMethodName={order.shipping_method_name}
            trackingNumber={order.tracking_number}
            shippingCompany={order.shipping_company}
            onSave={(data) => shippingMutation.mutateAsync(data)}
            isSaving={shippingMutation.isPending}
          />

          <ShipmentsHistory shipments={shipments} />
        </div>
      </div>
    </div>
  );
}
